/**
 * "Tạo dịch vụ" on a customer: staff order a new service from a plan on sale (owner decision
 * 2026-10-05 §10). The price is the customer's; the total is held on the customer's wallet (every
 * payment is a wallet top-up, §1), the service is provisioned and the invoice follows once it is
 * active (§2). A wallet short of the total refuses the order with the amount to top up.
 */

import { Server } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { CycleCode, ServiceQuote } from '../../api/areas/catalog';
import type { NewServiceOrder } from '../../api/areas/subscriptions';
import { Callout, Field } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { chosenOptions, formatVnd, optionGroupsOf, sortCycles, type OptionPick } from '../../lib/catalog';
import { errorText } from '../../lib/errorText';
import { useApi } from '../../lib/useApi';
import { OptionPicker } from '../catalog/OptionPicker';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';

function OrderForm({ close, changed, customer }: DialogBodyProps & { customer: { id: string; name: string } }) {
  const { t, i18n } = useTranslation('catalog');
  const en = i18n.language.startsWith('en');
  const name = (x: { name_vi: string; name_en: string }) => (en ? x.name_en : x.name_vi);
  const products = useApi((signal) => api.listCatalogProducts(null, { signal }), []);
  const [product, setProduct] = useState('');
  const detail = useApi((signal) => (product ? api.getCatalogProduct(product, { signal }) : Promise.resolve(null)), [product]);
  const [plan, setPlan] = useState('');
  const planDetail = useApi((signal) => (plan ? api.getCatalogPlan(plan, { signal }) : Promise.resolve(null)), [plan]);
  const [cycle, setCycle] = useState<CycleCode | ''>('');
  const [location, setLocation] = useState('');
  const [label, setLabel] = useState('');
  const [username, setUsername] = useState('');
  const [promo, setPromo] = useState('');
  const [picks, setPicks] = useState<Record<string, OptionPick>>({});
  const [reason, setReason] = useState('');
  const [quote, setQuote] = useState<{ q?: ServiceQuote; error?: unknown } | null>(null);

  const onSale = (products.status === 'success' ? products.data.products : []).filter((p) => p.visibility === 'PUBLIC');
  const chosenProduct = onSale.find((p) => p.id === product);
  const plans = (detail.status === 'success' && detail.data ? detail.data.plans : []).filter((p) => p.visibility === 'PUBLIC');
  const chosenPlan = plans.find((p) => p.id === plan);
  const cycles = chosenPlan ? sortCycles(chosenPlan.cycles) : [];
  const optionGroups = chosenPlan && detail.status === 'success' && detail.data ? optionGroupsOf(detail.data.options, chosenPlan.id) : [];
  // Only the locations a buyer may pick (`customer_choice`); none: the server places the service and a
  // named location would be refused LOCATION_NOT_OFFERED.
  const locations = planDetail.status === 'success' && planDetail.data ? [...new Set(planDetail.data.stock.filter((s) => s.customer_choice).map((s) => s.location_code))] : [];
  // The branches whose service is named by a domain (the DNS core refuses a zone apex that is not one).
  const needsDomain = chosenProduct?.branch === 'hosting' || chosenProduct?.branch === 'website' || chosenProduct?.branch === 'email' || chosenProduct?.branch === 'dns';
  // The cPanel login a hosting account may get, checked by the hosting core on each connection of the
  // plan's server group that takes new accounts (the panel's rules, no account has it); checked again
  // at delivery.
  const isHosting = chosenProduct?.branch === 'hosting';
  const login = username.trim();
  const pd = planDetail.status === 'success' ? planDetail.data : null;
  const fulfilment = pd?.fulfilment ?? null;
  const members = (fulfilment && pd?.placement_groups.find((g) => g.code === fulfilment.placement_group)?.members) || [];
  const targets = isHosting && fulfilment ? [...new Set(members.filter((m) => m.accepting && m.module === fulfilment.module).map((m) => m.instance_key))] : [];
  const loginCheck = useApi(
    (signal) => (login && fulfilment && targets.length ? Promise.all(targets.map((k) => api.checkHostingUsername(fulfilment.module, k, login, { signal }))) : Promise.resolve(null)),
    [login, fulfilment?.module, targets.join('\n')],
  );
  const loginOk = !login || loginCheck.status === 'success';
  const body = (): Omit<NewServiceOrder, 'reason'> => ({
    customer_id: customer.id,
    plan_id: plan,
    cycle,
    location: location || (locations.length === 1 ? locations[0] : null),
    promotion_code: promo.trim() || null,
    options: chosenOptions(optionGroups, picks),
    label: label.trim() || null,
    config: isHosting && login ? { username: login } : undefined,
  });
  const ready = Boolean(plan && cycle && (!needsDomain || label.trim()) && (locations.length <= 1 || location) && (!isHosting || loginOk));
  const price = () => {
    setQuote(null);
    const b = body();
    api.quoteService({ plan_id: b.plan_id, cycle: b.cycle as CycleCode, action: 'NEW', customer_id: customer.id, location: b.location, promotion_code: b.promotion_code, options: b.options }).then(
      (q) => setQuote({ q }),
      (error: unknown) => setQuote({ error }),
    );
  };
  const w = useWrite((b: NewServiceOrder, opts) => api.orderService(b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ ...body(), reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{t('order.placed', { description: w.state.result.description, total: formatVnd(w.state.result.total) })}</Callout>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('order.product')}>
              <select
                className={selectClass}
                value={product}
                onChange={(e) => {
                  setProduct(e.target.value);
                  setUsername('');
                  setPlan('');
                  setCycle('');
                  setLocation('');
                  setPicks({});
                  setQuote(null);
                }}
                required
              >
                <option value="">{t('order.choose')}</option>
                {onSale.map((p) => (
                  <option key={p.id} value={p.id}>
                    {name(p)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('order.plan')}>
              <select
                className={selectClass}
                value={plan}
                onChange={(e) => {
                  setPlan(e.target.value);
                  setCycle('');
                  setLocation('');
                  setPicks({});
                  setQuote(null);
                }}
                disabled={!product}
                required
              >
                <option value="">{t('order.choose')}</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {name(p)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('order.cycle')}>
              <select
                className={selectClass}
                value={cycle}
                onChange={(e) => {
                  setCycle(e.target.value as CycleCode);
                  setQuote(null);
                }}
                disabled={!plan}
                required
              >
                <option value="">{t('order.choose')}</option>
                {cycles.map((c) => (
                  <option key={c} value={c}>
                    {t(`cycle.${c}`)}
                  </option>
                ))}
              </select>
            </Field>
            {locations.length > 1 ? (
              <Field label={t('order.location')}>
                <select className={selectClass} value={location} onChange={(e) => setLocation(e.target.value)} required>
                  <option value="">{t('order.choose')}</option>
                  {locations.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label={needsDomain ? t('order.domain') : t('order.label')} hint={needsDomain ? t('order.domainHint') : t('order.labelHint')}>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} className={needsDomain ? 'font-mono' : undefined} required={needsDomain} maxLength={253} />
            </Field>
            {isHosting ? (
              <Field
                label={t('order.username')}
                hint={
                  loginOk ? t('order.usernameHint') : loginCheck.status === 'loading' ? t('order.usernameChecking') : <span className="text-destructive">{errorText(loginCheck.error).text}</span>
                }
              >
                <Input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} className="font-mono" maxLength={16} autoComplete="off" aria-invalid={loginCheck.status === 'error'} />
              </Field>
            ) : null}
            <Field label={t('order.promotion')}>
              <Input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} className="font-mono" maxLength={64} />
            </Field>
          </div>
          {optionGroups.length ? (
            <fieldset className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
              <legend className="px-1 text-sm font-medium">{t('order.options')}</legend>
              <OptionPicker
                groups={optionGroups}
                picks={picks}
                onChange={(p) => {
                  setPicks(p);
                  setQuote(null);
                }}
              />
            </fieldset>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" disabled={!ready} onClick={price}>
              {t('order.price')}
            </Button>
            {quote?.q ? (
              <span className="text-sm">
                {t('order.total', { total: formatVnd(quote.q.total.amount), tax: formatVnd(quote.q.tax.amount) })}
              </span>
            ) : null}
          </div>
          {quote?.error ? <WriteError error={quote.error} /> : null}
          <Callout tone="info">{t('order.walletNote', { customer: customer.name })}</Callout>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!ready || !reason.trim()} submit={w.state.phase === 'done' ? undefined : t('order.submit')} onClose={close} />
    </form>
  );
}

/** "Tạo dịch vụ" (customer page). */
export function NewServiceOrderButton({
  customer,
  onChanged,
  ...control
}: {
  customer: { id: string; name: string };
  onChanged?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: boolean;
}) {
  const { t } = useTranslation('catalog');
  return (
    <ActionDialog label={t('order.button')} icon={Server} title={t('order.title', { customer: customer.name })} description={t('order.description')} wide onChanged={onChanged} {...control}>
      {(p) => <OrderForm {...p} customer={customer} />}
    </ActionDialog>
  );
}
